import { errorToast } from '@/utils';
import { Button, Collapse, Flex, Form } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  useGetAllDepartmentsQuery,
  useGetAllDesignationsQuery,
  useGetEmployeeDetailsQuery,
  useUpdateEmployeeMutation,
} from '../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import PageTitle from '../../../../components/PageTitle';
import WithLoader from '../../../../components/WithLoader';
import DashboardLayout from '../../../../layouts/DashboardLayout';
import { toast } from '../../../../lib/toast';
import { normalizePhoneNumber } from '../../../../utils/string.utils';
import CustomFieldInputs from './_components/shared/CustomFieldInputs';
import {
  CUSTOM_FIELDS_KEY,
  useActiveCustomFields,
} from './_components/shared/custom-fields.helpers';
import JobInformation from './_components/UpdateEmployee/JobInformation';
import PayrollInformation from './_components/UpdateEmployee/PayrollInformation';
import PersonalInformation from './_components/UpdateEmployee/PersonalInformation';

const { Panel } = Collapse;

const headerRenderer = (title: string) => (
  <div className="flex items-center justify-between w-full">
    <div className="justify-start text-white text-xl font-bold">{title}</div>
  </div>
);

// Fields belonging to each panel — sourced directly from each section's Form.Item names
const PANEL_FIELDS: Record<string, string[]> = {
  // Panel 1: PersonalInformation
  '1': [
    'name',
    'phone',
    'email',
    'date_of_birth',
    'blood_group',
    'house_address',
    'emergency_contacts',
  ],
  // Panel 2: JobInformation
  '2': [
    'joining_date',
    'employee_type',
    'manager_id',
    'employee_id',
    'office_id',
    'is_geo_enable',
    'flexi_hour',
    'is_face_recognition_enable',
    'is_overtime_enable',
    'fence_attendance_permission',
    'overtime_policy_id',
    'department_id',
    'designation_id',
    'grade',
    'salary',
    // JTI: a field missing from this list is silently dropped on save.
    'payBasis',
    'dailyRateBdt',
  ],
  // Panel 4: the company's own extra fields, all under one key
  '4': [CUSTOM_FIELDS_KEY],
  // Panel 3: PayrollInformation
  '3': [
    'bank_name',
    'account_name',
    'account_number',
    'branch',
    'routing_number',
    'tin',
    'nid',
  ],
};

export default function UpdateEmployee() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  // All panels open by default
  const [activeKey, setActiveKey] = useState<string[]>(['1']);

  const { data: employeeData, isLoading } = useGetEmployeeDetailsQuery(
    String(id),
    { skip: !id },
  );

  // Get departments and designations for filter dropdowns
  const { data: departmentsData, isLoading: isDepartmentLoading } =
    useGetAllDepartmentsQuery(
      { params: { status: 'active' } },
      { skip: !open },
    );
  const { data: designationsData, isLoading: isDesignationLoading } =
    useGetAllDesignationsQuery(
      { params: { status: 'active' } },
      { skip: !open },
    );

  // Create employee mutation
  const [updateEmployee, { isLoading: updating }] = useUpdateEmployeeMutation();

  // Companies without custom fields never see the extra panel.
  const { fields: customFields } = useActiveCustomFields();

  const handleSubmit = async (values: any) => {
    const { date_of_birth, emergency_contacts, phone, ...rest } = values || {};
    try {
      // Transform form data to match API expectations
      const employeeData = {
        ...rest,
        phone: normalizePhoneNumber(phone),
        date_of_birth: date_of_birth || null,
        emergency_contacts: emergency_contacts?.map(
          (el: Record<string, any>) => ({
            ...el,
            mobile: normalizePhoneNumber(el?.mobile),
          }),
        ),
      };

      await updateEmployee({ data: employeeData, id }).unwrap();

      toast('success', 'Employee updated successfully');
      form.resetFields();
      navigate(`/admin/employee/dashboard/employee-details/${id}`);
    } catch (error) {
      // Error handled by useError hook
      errorToast(error, 'Employee update failed.');
    }
  };

  const handleFinishFailed = () => {
    // Ask the form which fields currently have errors
    const fieldsWithErrors = form
      .getFieldsError()
      .filter((f) => f.errors.length > 0)
      .map((f) => String(f.name[0]));

    // Find which panels contain at least one errored field
    const panelsToOpen = Object.entries(PANEL_FIELDS)
      .filter(([, fields]) => fields.some((f) => fieldsWithErrors.includes(f)))
      .map(([key]) => key);

    // Open those panels while keeping any already-open ones
    setActiveKey((prev) => [...new Set([...prev, ...panelsToOpen])]);
  };

  useEffect(() => {
    const employee = employeeData?.data?.data;
    if (employee) {
      const { date_of_birth, joining_date, emergency_contacts, ...others } =
        employee || {};

      form.setFieldsValue({
        ...others,
        date_of_birth: date_of_birth ? dayjs(employee?.date_of_birth) : null,
        joining_date: joining_date ? dayjs(employee?.joining_date) : null,
        employee_type: employee?.employee_type ? employee?.employee_type : null,
        // JTI: every existing employee is monthly until told otherwise.
        payBasis: employee?.pay_basis || 'MONTHLY',
        dailyRateBdt: employee?.daily_rate_bdt ?? undefined,
        employee_id: employee?.employee_id ? employee?.employee_id : null,
        manager_id: employee?.manager_id ? employee?.manager_id : null,
        emergency_contacts:
          emergency_contacts &&
          Array.isArray(emergency_contacts) &&
          emergency_contacts.length > 0
            ? emergency_contacts
            : [{ relation: undefined, name: '', mobile: '' }],
      });

      // ✅ IMPORTANT: get values AFTER setting them
      const values = form.getFieldsValue(true);
      void values; // no longer needed for button state
    }
  }, [employeeData, form]);

  return (
    <>
      <DashboardLayout title="Employee">
        <Form
          form={form}
          size="large"
          layout="vertical"
          className="custom-form"
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
        >
          <Flex
            align="center"
            justify="space-between"
            className="block lg:flex"
          >
            <PageTitle
              title="Employee Profile"
              items={[
                { title: 'Dashboard', path: '/admin/employee/dashboard' },
                {
                  title: 'Employee Directory',
                  path: '/admin/employee/dashboard',
                },
                {
                  title: 'Profile',
                  path: `/admin/employee/dashboard/employee-details/${id}`,
                },
                {
                  title: 'Edit Profile',
                },
              ]}
            />

            <Flex
              align="center"
              justify="space-start"
              gap={16}
              className="mt-4 lg:mt-0"
            >
              <Link to={`/admin/employee/dashboard/employee-details/${id}`}>
                <Button
                  size="middle"
                  className="default !text-white !text-base !font-semibold !bg-transparent !border !border-gray-700"
                >
                  Cancel
                </Button>
              </Link>

              <Button
                size="middle"
                htmlType="submit"
                loading={updating}
                className="default !text-[#444444] !text-base !font-semibold !bg-[#00C6AE] !border !border-[#00C6AE]"
              >
                Save & Update
              </Button>
            </Flex>
          </Flex>

          <WithLoader
            isLoading={isLoading || isDepartmentLoading || isDesignationLoading}
          >
            <div className="pt-6 pb-6">
              <div className="mx-auto w-full">
                <div className="rounded-lg overflow-hidden shadow-md">
                  <Collapse
                    activeKey={activeKey}
                    onChange={(keys) => setActiveKey(keys as string[])}
                    accordion={false}
                    bordered={false}
                    expandIconPosition="right"
                    className="custom-collapse"
                  >
                    <Panel
                      forceRender={true}
                      header={headerRenderer('Personal Information')}
                      key="1"
                      className="!border !border-gray-700 mb-3 p-3"
                      style={{
                        borderRadius: 8,
                      }}
                    >
                      <PersonalInformation form={form} />
                    </Panel>

                    {/* Additional panels matching the image */}
                    <Panel
                      forceRender={true}
                      header={headerRenderer('Job Information')}
                      key="2"
                      className="!border !border-gray-700 mb-3 p-3"
                      style={{
                        borderRadius: 8,
                      }}
                    >
                      <JobInformation
                        departmentsData={departmentsData}
                        designationsData={designationsData}
                      />
                    </Panel>

                    <Panel
                      key="3"
                      forceRender={true}
                      header={headerRenderer('Payroll Information')}
                      className="!border !border-gray-700 mb-3 p-3"
                      style={{
                        borderRadius: 8,
                      }}
                    >
                      <PayrollInformation />
                    </Panel>

                    {/* The company's own extra fields, if it defined any */}
                    {customFields.length > 0 && (
                      <Panel
                        key="4"
                        forceRender={true}
                        header={headerRenderer('Additional Information')}
                        className="!border !border-gray-700 mb-3 p-3"
                        style={{
                          borderRadius: 8,
                        }}
                      >
                        <CustomFieldInputs />
                      </Panel>
                    )}
                    {/* <Panel
                    forceRender={true}
                    header={headerRenderer('Access Configuration')}
                    key="3"
                    className="!border !border-gray-700 mb-3 p-3"
                    style={{
                      borderRadius: 8,
                    }}
                  >
                    <AccessConfiguration />
                  </Panel>

                  <Panel
                  forceRender={true}
                    header={headerRenderer('Payroll Information')}
                    key="4"
                    className="!border !border-gray-700 mb-3 p-3"
                    style={{
                      borderRadius: 8,
                    }}
                  >
                    <PayrollInformation />
                  </Panel>

                  <Panel
                  forceRender={true}
                    header={headerRenderer('Documents Upload')}
                    key="5"
                    className="!border !border-gray-700 mb-3 p-3"
                    style={{
                      borderRadius: 8,
                    }}
                  >
                    <DocumentsUpload />
                  </Panel> */}
                  </Collapse>
                </div>
              </div>
            </div>
          </WithLoader>
        </Form>
      </DashboardLayout>
    </>
  );
}
